import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import useAPI from 'src/hooks/useAPI'
import useValid from 'src/hooks/useValid'
import { CertNumState, RecoilStateList, TelTypeState, uiData } from 'src/recoil/common'
import Timer from 'src/components/views/mkt04/Timer'
import { JoinFormType, PramsKeys } from 'src/types/type'
import { useTelList } from 'src/hooks/useTelList'
import * as S from 'src/styles/styledcomponent/views/mkt04/PhoneVerification'
import { useCheckTelecomIsBlock } from 'src/hooks/useCheckTelecomIsBlock'
import { Button } from 'src/components/views/mkt04/Button'
import useMoveScrollTo from 'src/hooks/useMoveScrollTo'
import ArrowRight from 'src/assets/images/mkt04/arrow_rt.svg'
import { useFormContext } from 'react-hook-form'
import { useAutoInputGuard } from 'src/hooks/useAutoInputGuard'

const PhoneVerification = (props: any): JSX.Element => {
  const { onMoveToElement, RefInput, submit } = props
  const joinLoading = submit.joinLoading
  const telSectionRef = useRef<HTMLDivElement>(null)
  const { RefInput: certInputRef, onMoveToElement: moveTelecomAndFocusCert } =
    useMoveScrollTo(telSectionRef)
  const { pcode } = useParams<PramsKeys>()
  const telList = useTelList()
  const handleValid = useValid()
  const CertApi = useAPI('requestSendCertNumber')
  const JoinApi = useAPI('requestJoin')
  const {
    TelType,
    PhoneNum,
    CertNum,
    CertCheck,
    Timeout,
    termsAgree,
    authKeyValue,
    TransId,
    TermsData,
  } = useRecoilValue(RecoilStateList)

  const isPhoneValid = PhoneNum.length === 11
  const isCertValid = /^\d{4}$/.test(CertNum)
  const [phoneDirty, setPhoneDirty] = useState(false)
  const [certDirty, setCertDirty] = useState(false)
  const [cooldown, setCooldown] = useState<number>(0)
  const [showTooltip, setShowTooltip] = useState(false)
  const didAutoFocusRef = useRef(false)
  const { register, setFocus } = useFormContext<JoinFormType>()
  const approvalReg = register('ApprovalNumber')
  const pnumberReg = register('PNumber')

  useEffect(() => {
    if (CertCheck) {
      setCooldown(10)
      setShowTooltip(false)
    }
  }, [CertCheck, Timeout])

  useEffect(() => {
    if (cooldown <= 0) return
    const id = setInterval(() => {
      setCooldown((prevCooldown) => {
        if (prevCooldown <= 1) {
          return 0
        }
        return prevCooldown - 1
      })
    }, 3000)

    return () => clearInterval(id)
  }, [cooldown])

  useEffect(() => {
    const len = CertNum?.length ?? 0
    if (cooldown === 0 && CertCheck && len < 4) {
      setShowTooltip(true)
    } else if (len === 4) {
      setShowTooltip(false)
    }
  }, [cooldown, CertCheck, CertNum])

  useEffect(() => {
    if (!CertCheck) return

    const focusCert = () => {
      setFocus('ApprovalNumber', { shouldSelect: true })
      const el = document.getElementById('certInput') as HTMLInputElement | null
      if (el) {
        el.focus()
        try {
          el.setSelectionRange(el.value.length, el.value.length)
        } catch {}
      }
    }

    requestAnimationFrame(() => setTimeout(focusCert, 80))

    const prev = window.onfocus
    window.onfocus = () => {
      setTimeout(focusCert, 120)
      window.onfocus = prev || null
    }
  }, [CertCheck, setFocus])

  const phoneInvalid = phoneDirty && !isPhoneValid
  const certInvalid = certDirty && !isCertValid
  useCheckTelecomIsBlock()

  type Stage = 'telecom' | 'phone' | 'cert'
  type SectionState = 'active' | 'done' | undefined

  const stage: Stage = (() => {
    if (CertCheck) return 'cert'
    if (TelType !== null && TelType !== undefined) return 'phone'
    return 'telecom'
  })()

  const setTelType = useSetRecoilState(TelTypeState)
  const ResetCertNum = useResetRecoilState(CertNumState)
  const {
    layoutsConfirm: { submitBtn },
    layoutsInsert: {
      certs: { backgroundColor: certsBgColor },
      agency: { actColor },
    },
  } = useRecoilValue(uiData)

  const stateOf = (key: Stage): SectionState => {
    if (joinLoading && key === 'cert') {
      return 'done'
    }

    if (stage === key) {
      if (key === 'phone' && PhoneNum.length !== 11) return undefined
      return 'active'
    }

    if (stage === 'cert' && (key === 'telecom' || key === 'phone')) {
      return 'done'
    }

    return undefined
  }
  const { mutateAsync: getSendOtp, isLoading: certLoading } = CertApi.useSetMutation({
    Telecom: TelType,
    PNumber: PhoneNum,
    Pcode: pcode,
  })
  const { mutateAsync: JoinRequest } = JoinApi.useSetMutation({
    TranID: TransId,
    Telecom: TelType,
    PNumber: PhoneNum,
    PCode: pcode,
    ApprovalNumber: Number(CertNum),
    TermsIdx: TermsData?.TermsIdx,
    TermsAgree: termsAgree.join(''),
    authKey: authKeyValue,
  })

  const requestCert = () => {
    ResetCertNum()
    setFocus('ApprovalNumber', { shouldSelect: true })

    const armRefocus = () => {
      const prev = window.onfocus
      window.onfocus = () => {
        setTimeout(() => setFocus('ApprovalNumber', { shouldSelect: true }), 150)
        window.onfocus = prev || null
      }
    }

    submit
      .validationCert(getSendOtp, { resend: false })
      .catch(() => {})
      .finally(() => {
        setTimeout(() => setFocus('ApprovalNumber', { shouldSelect: true }), 120)
        armRefocus()
      })
  }

  const requestRecert = () => {
    ResetCertNum()
    setFocus('ApprovalNumber', { shouldSelect: true })

    const prev = window.onfocus
    window.onfocus = () => {
      setTimeout(() => setFocus('ApprovalNumber', { shouldSelect: true }), 150)
      window.onfocus = prev || null
    }

    submit
      .validationCert(getSendOtp, { resend: true })
      .catch(() => {})
      .finally(() => {
        setTimeout(() => setFocus('ApprovalNumber', { shouldSelect: true }), 120)
      })
  }

  const TelecomSelector = async (option: number) => {
    if (certLoading || joinLoading || CertCheck) return
    setTelType(option)
    setPhoneDirty(true)
    onMoveToElement()
    setFocus('PNumber', { shouldSelect: true })
    setTimeout(() => setFocus('PNumber'), 120)
  }

  const PREFIX = '010'
  const REST_MAX = 8

  const onlyDigits = (s: string) => s.replace(/\D/g, '')

  const formatRest = (digits: string) => {
    const d = onlyDigits(digits).slice(0, REST_MAX)
    if (d.length <= 4) return d
    return `${d.slice(0, 4)} ${d.slice(4)}`
  }

  const parseRestFromFull = (full: string) =>
    onlyDigits(full).startsWith(PREFIX)
      ? onlyDigits(full).slice(PREFIX.length, PREFIX.length + REST_MAX)
      : onlyDigits(full).slice(0, REST_MAX)

  const handlePhoneEnterKey: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      ;(e.currentTarget as HTMLInputElement).blur()
      setTimeout(() => {
        if (!certLoading && PhoneNum.length === 11) requestCert()
      }, 50)
    }
  }

  const handleCertSubmit = () => {
    const el = document.getElementById('certInput') as HTMLInputElement
    if (!el) return

    el.blur()
    setTimeout(() => {
      if (!joinLoading && /^\d{4}$/.test(el.value.trim())) {
        submit.join(JoinRequest)
      }
    }, 50)
  }

  const handleCertEnterKey = useAutoInputGuard(handleCertSubmit)

  useEffect(() => {
    if (stage !== 'phone' || CertCheck) return
    if (didAutoFocusRef.current) return
    didAutoFocusRef.current = true
    setPhoneDirty(true)
    const doFocus = () => {
      setFocus('PNumber', { shouldSelect: true })

      const el = document.querySelector<HTMLInputElement>('.ipt_phonenumber')
      if (el) {
        el.focus()
        try {
          el.setSelectionRange(el.value.length, el.value.length)
        } catch {}
      }

      const prev = window.onfocus
      window.onfocus = () => {
        setTimeout(() => {
          setFocus('PNumber', { shouldSelect: true })
          const el2 = document.querySelector<HTMLInputElement>('.ipt_phonenumber')
          el2?.focus()
        }, 120)
        window.onfocus = prev || null
      }
    }

    requestAnimationFrame(() => setTimeout(doFocus, 80))
  }, [stage, CertCheck, setFocus])

  return (
    <S.PhoneVerificationWrap
      backgroundColor={certsBgColor}
      borderColor={actColor}
      $isCertStage={CertCheck}
      ref={props.RefElement}
    >
      <S.Division $state={stateOf('telecom')}>
        <S.SectionTitle>
          <span>통신사</span>를 선택해주세요
        </S.SectionTitle>
        <S.TelSelectorWrap ref={telSectionRef}>
          {telList?.map((element) => (
            <S.TelSelect
              bgActive={TelType === element.idx}
              actColor={actColor}
              key={element.idx}
              onClick={() => TelecomSelector(element.idx)}
              disabled={CertCheck}
            >
              <S.Telecoms
                className={element.text}
                bgActive={TelType === element.idx}
                oneTelStyle={telList?.length === 1}
                actColor={actColor}
              />
            </S.TelSelect>
          ))}
        </S.TelSelectorWrap>
      </S.Division>
      <S.Division $state={stateOf('phone')}>
        <S.SectionTitle $invalid={phoneInvalid}>
          <span>휴대폰번호</span>를 입력해주세요
        </S.SectionTitle>
        <S.PhoneInputBox $invalid={phoneInvalid} $actColor={actColor}>
          <S.Prefix $disabled={CertCheck}>010</S.Prefix>
          <S.TelInput
            {...pnumberReg}
            ref={(el) => {
              pnumberReg.ref(el)
              if (RefInput) RefInput.current = el
            }}
            className="ipt_phonenumber"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="1234 5678"
            value={formatRest(parseRestFromFull(PhoneNum))}
            onChange={(e) => {
              pnumberReg.onChange(e)
              if (certLoading || joinLoading) return
              const rest = onlyDigits(e.target.value).slice(0, REST_MAX)
              const next = PREFIX + rest
              handleValid.phoneNumValid({
                target: { value: next },
              } as any)
            }}
            onKeyDown={handlePhoneEnterKey}
            disabled={CertCheck}
            onClick={onMoveToElement}
          />
          <S.ClearBtn
            type="button"
            aria-label="입력 지우기"
            $visible={!CertCheck && parseRestFromFull(PhoneNum).length > 0}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              handleValid.phoneNumValid({ target: { value: '' } } as any)
              RefInput?.current?.focus?.()
            }}
          />
        </S.PhoneInputBox>
        {!CertCheck && (
          <Button.OtpCertRequest
            {...submitBtn}
            onMouseDown={() => {
              setFocus('ApprovalNumber', {
                shouldSelect: true,
              })
            }}
            onTouchStart={() => {
              setFocus('ApprovalNumber', { shouldSelect: true })
            }}
            onClick={requestCert}
            disabled={PhoneNum.length !== 11 || certLoading}
            isLoading={certLoading}
            iconSrc={ArrowRight}
            iconAlt=""
            iconSize={24}
          ></Button.OtpCertRequest>
        )}
      </S.Division>
      {CertCheck && (
        <S.Division $state={stateOf('cert')}>
          <S.SectionTitle $invalid={certInvalid}>
            <span>인증번호</span>를 입력해주세요
          </S.SectionTitle>
          <S.CertInputContainer $invalid={certInvalid} $actColor={actColor}>
            <S.CertInput
              {...approvalReg}
              ref={(el) => {
                approvalReg.ref(el)
                if (certInputRef) certInputRef.current = el
              }}
              id="certInput"
              className="ipt_smscode"
              type="tel"
              placeholder="4자리"
              maxLength={4}
              value={CertNum}
              onChange={(e) => {
                handleCertEnterKey({
                  key: '',
                  nativeEvent: { isTrusted: true },
                  currentTarget: e.currentTarget,
                } as any)

                approvalReg.onChange(e)
                if (joinLoading) return
                setCertDirty(true)
                handleValid.certNumValid(e)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                }
                handleCertEnterKey(e)
              }}
              autoComplete="one-time-code"
              onFocus={(e) => {
                setCertDirty(true)
                setTimeout(moveTelecomAndFocusCert, 0)
              }}
            />
            <S.CertInputInnerBox>
              <Timer time={Timeout} />
              <div style={{ position: 'relative' }}>
                <Button.RetryOtpCertRequest
                  onClick={requestRecert}
                  disabled={certLoading || cooldown > 0}
                  isLoading={certLoading}
                />

                {showTooltip && cooldown === 0 && (CertNum?.length ?? 0) < 4 && (
                  <S.ResendTooltip role="status">
                    문자가 안오면
                    <br />
                    <span>재발송</span>을 누르세요!
                  </S.ResendTooltip>
                )}
              </div>
            </S.CertInputInnerBox>
          </S.CertInputContainer>
        </S.Division>
      )}
    </S.PhoneVerificationWrap>
  )
}

export default PhoneVerification
